import React, { useState } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Container,
  Button,
  useScrollTrigger,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import { useLocation } from "react-router-dom";
import { useThemeContext } from "../contexts/ThemeContext.tsx";
import logo from "../assets/images/logo.png";
import MobileMenu from "./MobileMenu.tsx";

// Styled components
const StyledAppBar = styled(AppBar)<{ $triggered: boolean; $isHomePage: boolean; component?: React.ElementType }>(({ theme, $triggered, $isHomePage }) => ({
  position: 'fixed',
  backgroundColor: $triggered || !$isHomePage ? theme.palette.background.paper : 'transparent',
  boxShadow: $triggered || !$isHomePage ? theme.shadows[1] : 'none',
  transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
  borderRadius: '0 0 12px 12px',
}));

const StyledContainer = styled(Container)(({ theme }) => ({
  maxWidth: 'lg',
}));

const StyledToolbar = styled(Toolbar)({
  justifyContent: 'space-between',
});

const StyledLogoContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

const StyledLogo = styled('img')(({ theme }) => ({
  height: 50,
  marginRight: theme.spacing(2),
}));

const StyledLogoText = styled(Typography)<{ $textColor: string; component?: React.ElementType }>(({ theme, $textColor }) => ({
  display: 'none',
  fontWeight: 'bold',
  color: $textColor,
  [theme.breakpoints.up('sm')]: {
    display: 'block',
  },
}));

const StyledMobileMenuContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const StyledDesktopMenuContainer = styled(Box)(({ theme }) => ({
  display: 'none',
  alignItems: 'center',
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const StyledNavButton = styled(Button)<{ $textColor: string }>(({ theme, $textColor }) => ({
  margin: theme.spacing(1, 0),
  color: $textColor,
  fontWeight: 'bold',
}));

const StyledThemeToggle = styled(IconButton)<{ $textColor: string }>(({ theme, $textColor }) => ({
  marginLeft: theme.spacing(1),
  color: $textColor,
}));

const StyledBookButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(2),
}));

interface NavbarProps {
  onOpenBookingDialog: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({ onOpenBookingDialog }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { toggleColorMode, mode } = useThemeContext();
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  // Detect if user has scrolled down
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 50,
  });

  const handleOpenMobileMenu = () => {
    setMobileMenuOpen(true);
  };

  const handleCloseMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Function to scroll to section on homepage
  const scrollTo = (id: string) => {
    handleCloseMobileMenu();

    if (!isHomePage) {
      // Navigate to homepage first, then scroll after a delay
      window.location.href = `/#${id}`;
      return;
    }

    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Determine text color based on page and scroll position
  const getTextColor = () => {
    if (trigger || !isHomePage) {
      return "text.primary"; // Use primary text color when scrolled or not on homepage
    }
    return "primary.main"; // Use primary theme color (pink) when at top of homepage
  };

  return (
    <StyledAppBar
      $triggered={trigger}
      $isHomePage={isHomePage}
      component="nav"
      aria-label="Hoofdnavigatie"
    >
      <StyledContainer>
        <StyledToolbar disableGutters>
          {/* Logo and Title */}
          <StyledLogoContainer>
            <StyledLogo
              src={logo}
              alt="Pippaya Logo"
              role="img"
            />

            <StyledLogoText
              variant="h6"
              noWrap
              component="div"
              $textColor={getTextColor()}
            >
              Pippaya
            </StyledLogoText>
          </StyledLogoContainer>

          {/* Mobile menu */}
          <StyledMobileMenuContainer>
            <IconButton
              size="large"
              aria-label="menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenMobileMenu}
              color="inherit"
              sx={{ color: getTextColor() }}
            >
              <MenuIcon />
            </IconButton>
            <MobileMenu 
              open={mobileMenuOpen} 
              onClose={handleCloseMobileMenu}
              onOpenBookingDialog={onOpenBookingDialog}
              scrollTo={scrollTo}
            />
          </StyledMobileMenuContainer>

          {/* Desktop menu */}
          <StyledDesktopMenuContainer>
            <StyledThemeToggle
              onClick={toggleColorMode}
              color="inherit"
              aria-label={
                mode === "dark"
                  ? "Schakel naar lichte modus"
                  : "Schakel naar donkere modus"
              }
              $textColor={getTextColor()}
            >
              {mode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
            </StyledThemeToggle>
            <StyledNavButton
              onClick={() => scrollTo("home")}
              $textColor={getTextColor()}
            >
              Home
            </StyledNavButton>
            <StyledNavButton
              onClick={() => scrollTo("about")}
              $textColor={getTextColor()}
            >
              Over Ons
            </StyledNavButton>
            <StyledNavButton
              onClick={() => scrollTo("services")}
              $textColor={getTextColor()}
            >
              Diensten
            </StyledNavButton>
            <StyledBookButton
              variant="contained"
              color="secondary"
              onClick={onOpenBookingDialog}
            >
              Reservering aanvragen
            </StyledBookButton>
          </StyledDesktopMenuContainer>
        </StyledToolbar>
      </StyledContainer>
    </StyledAppBar>
  );
};

export default Navbar;
